import { useEffect, useRef, useState } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import {
  LOCALES,
  LOCALE_NAMES,
  LOCALE_FULL_NAMES,
  type Locale,
} from './translations';
import { cn } from '../lib/cn';

export function LanguageSwitcher({ className }: { className?: string }) {
  const { locale, setLocale, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t.nav.langLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5',
          'px-2.5 py-1.5 rounded-pill',
          'text-xs text-ink-400 hover:text-white transition-colors',
          'border border-transparent hover:border-white/10'
        )}
      >
        <Globe className="h-3.5 w-3.5" />
        <span>{LOCALE_NAMES[locale]}</span>
        <ChevronDown
          className={cn(
            'h-3 w-3 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.15, ease: [0.4, 0, 0, 1] }}
            role="listbox"
            className={cn(
              'absolute right-0 top-full mt-2 z-50 min-w-[160px]',
              'rounded-xl border border-white/10 bg-bg-elevated/95',
              'backdrop-blur-md shadow-card overflow-hidden'
            )}
          >
            {LOCALES.map((code: Locale) => {
              const isActive = code === locale;
              return (
                <li key={code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      setLocale(code);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between gap-3',
                      'px-3.5 py-2.5 text-sm text-left',
                      'hover:bg-white/5 transition-colors',
                      isActive ? 'text-white' : 'text-ink-400'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-xs text-brand-300">
                        {LOCALE_NAMES[code]}
                      </span>
                      <span>{LOCALE_FULL_NAMES[code]}</span>
                    </span>
                    {isActive && <Check className="h-3.5 w-3.5 text-brand-300" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
